import { useState, useMemo, useRef, useEffect } from "react";
import { T } from "../theme";
import { Button } from "./fmxkit";
import DataSheet from "./DataSheet";
import { getFieldTypeLabel, FMX_FIELD_TYPE_MAP } from "../fmxFieldTypes";
import { getSchemaModuleSlug, getBaseSchemaType } from "../schemas";
import fmxLogoBlue from "../assets/fmx-logo-blue.svg";

// Map fields — "A v3" (design_handoff_map_step_v3 + chrome_collapse).
// On top of the A v2 layout: the color-coded spreadsheet preview pins to the
// top while the mapping list scrolls beneath it (beneath the app's condensed
// chrome bar, which App renders — see WizardChrome.jsx), rule-applied rows
// wash teal and swap their select for a "Rule applied — <source> · Edit"
// control, and the unmapped section uses consistent column semantics: an
// FMX-field picker on the left, the static source column on the right.
// Resolved columns stay in the section so the row, spreadsheet header,
// status strip, and summary update atomically.

const ROW_GRID = { display: "grid", gridTemplateColumns: "15px 280px 14px 1fr", alignItems: "center", gap: 8 };

const TAG = {
  font: `500 10px/15px ${T.fontSans}`, color: T.inkSoft, background: T.bg,
  border: `1px solid ${T.borderSubtle}`, borderRadius: 3, padding: "0 4px", whiteSpace: "nowrap",
};

const SOURCE_SELECT = {
  width: "100%", height: 30, padding: "0 10px", boxSizing: "border-box", minWidth: 0,
  border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusBtn,
  background: T.white, color: T.ink, font: `500 13px ${T.fontData}`,
  cursor: "pointer", outline: "none",
};

const GROUP_LABEL = {
  font: `700 11px/16px ${T.fontSans}`, textTransform: "uppercase",
  letterSpacing: "0.07em", color: T.brand,
};

const CARD = { background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable };

const normName = s => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");

// Rough data-profile of a column's non-empty values, for the unmapped-columns triage.
function detectColumnType(values) {
  if (!values.length) return "Empty";
  const sample = values.slice(0, 100);
  const frac = re => sample.filter(v => re.test(v)).length / sample.length;
  if (frac(/^\d{1,2}\/\d{1,2}\/\d{2,4}$/) >= 0.9) return "Date (MM/DD/YYYY)";
  if (frac(/^\d{4}-\d{2}-\d{2}/) >= 0.9) return "Date (YYYY-MM-DD)";
  if (frac(/^-?\$?[\d,]+(\.\d+)?%?$/) >= 0.9) return "Number";
  const avgLen = sample.reduce((s, v) => s + v.length, 0) / sample.length;
  return avgLen > 30 ? "Free text" : "Text";
}

function joinNames(fields) {
  const names = fields.map(f => f.name);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function fileIcon(fileInfo) {
  const t = fileInfo?.type || "";
  if (t.includes("PDF")) return "file-pdf";
  if (t.includes("Excel") || t.includes("ODS")) return "file-excel";
  return "file-csv";
}

// Popover picker of FMX (receiving-schema) fields for an unmapped source column.
function FieldPicker({ value, isSkip, accent, options, suggested, schemaLabel, getTypeLabel, onPick, onSkip }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const label = value || (isSkip ? "Don't import this column" : null);
  return (
    <div ref={ref} onClick={e => e.stopPropagation()} style={{ position: "relative", minWidth: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", height: 30, border: `1px solid ${accent ? T.orange500 : T.borderStrong}`,
          borderRadius: T.radiusBtn, background: T.white, display: "flex", alignItems: "center", gap: 8,
          padding: "0 10px", font: `500 13px ${T.fontSans}`, color: label ? T.ink : T.inkSoft,
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label || "Choose FMX field…"}</span>
        <i className="fa-solid fa-caret-down" style={{ fontSize: 12, color: T.inkMuted }} />
      </button>
      {open && (
        <div style={{ position: "absolute", top: 34, left: 0, width: 300, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, boxShadow: T.shadowLg, zIndex: 40, padding: 4, maxHeight: 320, overflowY: "auto" }}>
          <div style={{ font: `700 10px/14px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.07em", color: T.inkSoft, padding: "6px 10px 4px" }}>{schemaLabel}</div>
          {options.length === 0 && (
            <div style={{ padding: "7px 10px", font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>No unmapped FMX fields left.</div>
          )}
          {options.map(o => (
            <button
              key={o.name}
              className="mig-menu-item"
              onClick={() => { onPick(o.name); setOpen(false); }}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: "none", background: value === o.name ? T.blue50 : "transparent", borderRadius: 4, cursor: "pointer", textAlign: "left" }}
            >
              <i className="fa-solid fa-check" style={{ fontSize: 10, color: T.brand, width: 12, flexShrink: 0, visibility: value === o.name ? "visible" : "hidden" }} />
              <span style={{ flex: 1, font: `500 13px ${T.fontSans}`, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {o.name}{o.required ? <span style={{ color: T.red500, fontWeight: 700 }}> *</span> : null}
              </span>
              {suggested === o.name && (
                <span style={{ font: `600 9.5px/14px ${T.fontSans}`, color: T.brand, background: T.blue50, border: `1px solid ${T.blue200}`, borderRadius: T.radiusPill, padding: "0 6px", flexShrink: 0 }}>Suggested</span>
              )}
              {getTypeLabel(o) && <span style={{ ...TAG, flexShrink: 0 }}>{getTypeLabel(o)}</span>}
            </button>
          ))}
          <div style={{ height: 1, background: T.borderSubtle, margin: "4px 6px" }} />
          <button
            className="mig-menu-item"
            onClick={() => { onSkip(); setOpen(false); }}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: "none", background: "transparent", borderRadius: 4, cursor: "pointer", textAlign: "left" }}
          >
            <i className="fa-solid fa-ban" style={{ fontSize: 10, color: T.inkMuted, width: 12, flexShrink: 0 }} />
            <span style={{ font: `500 13px ${T.fontSans}`, color: T.inkMuted }}>Don't import this column</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function StepMapFields({
  csv,
  schemaType,
  allFields,
  groupedFields,
  mapping,
  setMapping,
  transformRules,
  dynamicRates,
  setDynamicRates,
  rateTypeOptions = [],
  fileInfo,
  openTransformModal,
  memoryMatches,
  mappingSources,
  savedRules,
  depCacheMap,
  onCustomFieldTypeChange,
  onRerunMapping,
  aiLoading,
}) {
  // Cross-highlight state, keyed by source column name. Hover and pin are
  // independent; either activates the highlight on both sides.
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  // Triage decisions for columns Claude left unmapped. `assigned` keeps a
  // resolved column's row in the section (white + green) instead of moving it.
  const [skipped, setSkipped] = useState({});
  const [assigned, setAssigned] = useState({});
  const [hideEmptyUnmapped, setHideEmptyUnmapped] = useState(false);
  // Drag-and-drop column mapping: the column being dragged, the field row
  // under the cursor, the last drop per field ("Updated — now mapped from"),
  // and columns displaced by a drop ("was mapped to <field>").
  const [dragCol, setDragCol] = useState(null);
  const [dropField, setDropField] = useState(null);
  const [dropNotes, setDropNotes] = useState({});
  const [displaced, setDisplaced] = useState({});
  // Condensed-header / sticky-preview state, driven by the wizard scroll
  // container. Sticky offsets resolve against the scroller's padding edge,
  // so its top padding is subtracted to pin flush with the visible top.
  const [scrolled, setScrolled] = useState(false);
  const [stickyPad, setStickyPad] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    let el = rootRef.current?.parentElement;
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      el = el.parentElement;
    }
    if (!el || el === document.body) return;
    setStickyPad(parseFloat(getComputedStyle(el).paddingTop) || 0);
    const onScroll = () => setScrolled(el.scrollTop > 160);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const headers = csv.headers;
  const isActive = key => !!key && (hover === key || pinned === key);
  const togglePin = key => key && setPinned(p => (p === key ? null : key));

  // First FMX field mapped to each source column (drives column status).
  const fieldByCol = {};
  allFields.forEach(f => {
    const c = mapping[f.name];
    if (c && fieldByCol[c] === undefined) fieldByCol[c] = f;
  });

  const mappedCount = headers.filter(h => fieldByCol[h]).length;
  const unmappedHeaders = headers.filter(h => !fieldByCol[h]);
  const skippedCount = unmappedHeaders.filter(h => skipped[h]).length;
  const decideCount = unmappedHeaders.length - skippedCount;

  // Headline credits Claude's auto-mapping; the legend counts are live.
  // Re-snapshot after a "Re-run mapping" completes.
  const [autoMapped, setAutoMapped] = useState(mappedCount);
  const prevAiLoading = useRef(aiLoading);
  useEffect(() => {
    if (prevAiLoading.current && !aiLoading) {
      setAutoMapped(mappedCount);
      setAssigned({});
      setSkipped({});
    }
    prevAiLoading.current = aiLoading;
  }, [aiLoading, mappedCount]);

  // A column "assigned" from the triage section — only honored while the
  // mapping still points back at it (group selects can override it).
  const assignedField = h => (assigned[h] && mapping[assigned[h]] === h ? assigned[h] : null);

  // Per-column data profile (fill rate, uniques, samples, detected type).
  const colProfiles = useMemo(() => {
    const out = {};
    for (const h of csv.headers) {
      if (out[h]) continue;
      const values = csv.rows
        .map(r => r[h])
        .filter(v => v !== undefined && String(v).trim() !== "")
        .map(String);
      out[h] = {
        filled: values.length,
        unique: new Set(values).size,
        samples: [...new Set(values)].slice(0, 2),
        dtype: detectColumnType(values),
      };
    }
    return out;
  }, [csv]);

  // "Matches X of Y FMX <type> records" notes for reference fields, computed
  // against the project's live dependency caches when available.
  const refNotes = useMemo(() => {
    const moduleSlug = schemaType ? getSchemaModuleSlug(schemaType) : null;
    const out = {};
    for (const f of allFields) {
      const src = mapping[f.name];
      if (!f.crossSheet || !src) continue;
      const list = (moduleSlug && depCacheMap?.[`${f.crossSheet}:${moduleSlug}`]) || depCacheMap?.[f.crossSheet];
      if (!list?.length) continue;
      const names = new Set(list.map(n => String(n).toLowerCase().trim()));
      const uniq = [...new Set(
        csv.rows
          .map(r => r[src])
          .filter(v => v !== undefined && String(v).trim() !== "")
          .map(v => String(v).toLowerCase().trim())
      )];
      if (!uniq.length) continue;
      const matched = uniq.filter(v => names.has(v)).length;
      out[f.name] = `Matches ${matched} of ${uniq.length} FMX ${f.crossSheet} record${uniq.length === 1 ? "" : "s"}`;
    }
    return out;
  }, [allFields, mapping, depCacheMap, csv, schemaType]);

  const getTypeLabel = f => {
    if (f.crossSheet) return "Reference";
    if (f.isCustomField) return getFieldTypeLabel(f.fieldType);
    return { string: "Text", number: "Number", date: "Date", email: "Email", time: "Time", boolean: "Yes/No" }[f.type] || null;
  };

  const visibleGroups = Object.entries(groupedFields).filter(([group]) => group !== "Custom Fields");

  const requiredFields = allFields.filter(f => f.required);
  const unresolvedRequired = requiredFields.filter(f => !mapping[f.name] && !transformRules[f.name]);
  const allRequiredMapped = unresolvedRequired.length === 0;

  const fileName = fileInfo?.name || `${schemaType} data`;
  const baseType = schemaType ? getBaseSchemaType(schemaType) : "";

  // FMX fields a triage row can take: anything not already mapped or ruled,
  // plus the row's own current assignment so it shows checked in the popover.
  const pickerOptionsFor = h => allFields.filter(f =>
    f.group !== "Custom Fields"
    && ((!mapping[f.name] && !transformRules[f.name]) || mapping[f.name] === h)
  );
  const suggestionFor = (h, options) => {
    const n = normName(h);
    if (!n) return null;
    const exact = options.find(f => normName(f.name) === n);
    if (exact) return exact.name;
    const loose = options.find(f => normName(f.name).includes(n) || n.includes(normName(f.name)));
    return loose ? loose.name : null;
  };

  const assignField = (h, fieldName) => {
    setMapping(m => {
      const next = { ...m };
      const prev = assigned[h];
      if (prev && next[prev] === h) delete next[prev];
      next[fieldName] = h;
      return next;
    });
    setAssigned(a => ({ ...a, [h]: fieldName }));
    setSkipped(s => ({ ...s, [h]: false }));
  };

  const toggleSkip = h => {
    const fld = assignedField(h);
    if (!skipped[h] && fld) {
      setMapping(m => { const next = { ...m }; delete next[fld]; return next; });
      setAssigned(a => ({ ...a, [h]: null }));
    }
    setSkipped(s => ({ ...s, [h]: !s[h] }));
  };

  // Drop a dragged spreadsheet column onto a field row. The field's previous
  // source is displaced (flips orange in the sheet, joins the triage section);
  // dropping clears any prior skip/picker assignment for the dragged column.
  const dropOnField = (fieldName, col) => {
    if (!fieldName || !col) return;
    const prevCol = mapping[fieldName];
    setMapping(m => ({ ...m, [fieldName]: col }));
    if (prevCol && prevCol !== col) setDisplaced(d => ({ ...d, [prevCol]: fieldName }));
    setDisplaced(d => (d[col] ? { ...d, [col]: undefined } : d));
    setDropNotes(n => ({ ...n, [fieldName]: col }));
    setSkipped(s => ({ ...s, [col]: false }));
    setAssigned(a => (a[col] ? { ...a, [col]: null } : a));
    setDragCol(null);
    setDropField(null);
  };

  const noteLine = (color, icon, content) => (
    <div style={{ font: `400 11.5px/15px ${T.fontSans}`, color, padding: "4px 0 1px 23px", display: "flex", alignItems: "baseline", gap: 6 }}>
      <i className={`fa-solid fa-${icon}`} style={{ fontSize: 9, position: "relative", top: 0.5 }} />
      <span style={{ minWidth: 0 }}>{content}</span>
    </div>
  );

  // Triage section keeps resolved columns (assigned or skipped) in place.
  const sectionCols = headers.filter(h => !fieldByCol[h] || assignedField(h));
  const emptyUnmappedCount = sectionCols.filter(h => colProfiles[h]?.filled === 0 && !assignedField(h) && !skipped[h]).length;
  const displayedUnmapped = hideEmptyUnmapped
    ? sectionCols.filter(h => colProfiles[h]?.filled > 0 || assignedField(h) || skipped[h])
    : sectionCols;
  const innerRadius = T.radiusTable - 1;

  return (
    <>
      <style>{`.mig-menu-item:hover { background: ${T.blue50} !important; }`}</style>
      <div ref={rootRef} style={{ width: "100%", maxWidth: 1280, margin: "0 auto" }}>
        <p style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, margin: "0 0 14px" }}>
          <strong style={{ color: T.ink }}>{csv.rows.length} rows</strong>
          {" · "}
          <strong style={{ color: T.ink }}>{headers.length} columns</strong>
          {fileInfo?.name && <> · {fileInfo.name}</>}
          {fileInfo && fileInfo.type !== "CSV" && (
            <> · {fileInfo.type}{fileInfo.sheetName ? ` · Sheet: '${fileInfo.sheetName}'` : ""}</>
          )}
        </p>

        {schemaType === "Resource" && (
          <div style={{ ...CARD, marginBottom: 14, padding: "14px 16px" }}>
            <p style={{ ...GROUP_LABEL, margin: "0 0 4px" }}>Scheduling rate types</p>
            <p style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, margin: "0 0 10px" }}>
              Auto-detected from this site's user types. Each named rate type adds a Cost and a Unit
              field to the Scheduling Rates group below — matching the rate columns in the FMX
              import spreadsheet (e.g. Internal, Non-Profit, Community). Unit values are typically
              Hour or Event (FMX bills rates per hour / per event).
            </p>
            {dynamicRates.map((name, i) => {
              const trimmed = String(name || "").trim();
              const isDuplicate = trimmed && dynamicRates.findIndex(n => String(n || "").trim() === trimmed) !== i;
              // Soft warning only: discovery via user types can miss a rate type
              // that no user type references, so an unknown name may still be valid.
              const isUnknown = trimmed && !isDuplicate && rateTypeOptions.length > 0 &&
                !rateTypeOptions.some(o => String(o).trim().toLowerCase() === trimmed.toLowerCase());
              return (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <input
                    value={name}
                    placeholder="Rate type name (e.g. Internal)"
                    onChange={e => setDynamicRates(r => r.map((v, j) => (j === i ? e.target.value : v)))}
                    style={{ width: 240, height: 30, border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusBtn, padding: "0 10px", font: `400 13px/18px ${T.fontSans}`, color: T.ink }}
                  />
                  <span style={{ fontSize: 12, color: isDuplicate || isUnknown ? T.orangeText : T.inkMuted }}>
                    {isDuplicate
                      ? "duplicate — ignored"
                      : isUnknown
                        ? "not detected on this site — must exactly match an FMX scheduling rate type"
                        : trimmed
                          ? `adds "${trimmed} Cost" + "${trimmed} Unit"`
                          : "unnamed — no fields until named"}
                  </span>
                  <button className="fmx-btn-destructive" onClick={() => setDynamicRates(r => r.filter((_, j) => j !== i))} style={{ marginLeft: "auto" }}>Remove</button>
                </div>
              );
            })}
            <button className="fmx-btn-secondary" style={{ fontSize: 12, padding: "5px 14px" }} onClick={() => setDynamicRates(r => [...r, ""])}>
              + Add rate type
            </button>
          </div>
        )}

        {/* Status strip — counts react live to mapping and Skip/Undo */}
        <div style={{ ...CARD, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <span style={{ width: 34, height: 34, borderRadius: T.radiusPill, background: T.brand, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 14 }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 15px/20px ${T.fontSans}`, color: T.ink }}>
              Claude mapped {autoMapped} of your {headers.length} columns automatically.
            </div>
            <div style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, marginTop: 2 }}>
              Green columns are mapped.{" "}
              {decideCount > 0
                ? <><b style={{ color: T.orangeText }}>Orange</b> rows below need a decision — hover any column or row to see its match.</>
                : <>Hover any column or row to see its match.</>}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ display: "flex", height: 8, borderRadius: T.radiusPill, overflow: "hidden", width: 200 }}>
              {[
                { n: mappedCount, color: T.green500 },
                { n: decideCount, color: T.orange500 },
                { n: skippedCount, color: T.border },
              ].filter(s => s.n > 0).map((s, i) => (
                <div key={i} style={{ flex: s.n, background: s.color, marginLeft: i > 0 ? 2 : 0 }} />
              ))}
            </div>
            <div style={{ font: `500 12px/16px ${T.fontSans}`, color: T.inkMuted, marginTop: 6, display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.green500, marginRight: 5 }} />{mappedCount} mapped</span>
              {decideCount > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.orange500, marginRight: 5 }} />{decideCount} to decide</span>}
              {skippedCount > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.border, marginRight: 5 }} />{skippedCount} skipped</span>}
            </div>
          </div>
        </div>

        {/* Spreadsheet sheet — sticky beneath the compact header. Headers are
            draggable onto the mapping rows below; body pages/scrolls/searches
            through the whole file (shared DataSheet chrome). */}
        <div style={{ position: "sticky", top: 48 - stickyPad, zIndex: 20, marginBottom: 14 }}>
          <DataSheet
            icon={fileIcon(fileInfo)}
            title={fileName}
            meta={`${csv.rows.length} rows · ${headers.length} columns`}
            hint="Drag a column header onto a field below to map it — or hover to highlight"
            totalRows={csv.rows.length}
            matchRow={(i, ql) => headers.some(h => String(csv.rows[i][h] ?? "").toLowerCase().includes(ql))}
            defaultBodyHeight={118}
            footerNote=""
            elevated={scrolled}
          >
            {({ rowIndices, query }) => (
              <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", tableLayout: "fixed", minWidth: Math.max(640, headers.length * 88) }}>
                <thead>
                  <tr>
                    {headers.map((h, idx) => {
                      const field = fieldByCol[h];
                      const isSkip = !field && skipped[h];
                      const on = isActive(h);
                      return (
                        <th
                          key={idx}
                          draggable
                          onDragStart={e => { e.dataTransfer.setData("text/plain", h); e.dataTransfer.effectAllowed = "copy"; setDragCol(h); }}
                          onDragEnd={() => { setDragCol(null); setDropField(null); }}
                          onMouseEnter={() => setHover(h)}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => togglePin(h)}
                          style={{
                            padding: "7px 6px 6px", textAlign: "left", verticalAlign: "top",
                            background: isSkip ? T.bg : field ? T.tint.success : T.badge.orange,
                            borderBottom: `2px solid ${isSkip ? T.border : field ? T.green500 : T.orange500}`,
                            borderRight: `1px solid ${T.borderSubtle}`,
                            boxShadow: on ? `inset 0 0 0 2px ${T.brand}` : "none",
                            position: "sticky", top: 0, zIndex: 2,
                            cursor: "grab", opacity: dragCol === h ? 0.55 : 1,
                          }}
                        >
                          <div style={{ font: `700 11px/14px ${T.fontSans}`, color: isSkip ? T.inkSoft : T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <i className="fa-solid fa-grip-vertical" style={{ fontSize: 8, color: T.inkSoft, marginRight: 5, opacity: 0.7 }} />
                            {h === "" ? "(blank column)" : h}
                          </div>
                          <div style={{ font: `500 10px/14px ${T.fontSans}`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: isSkip ? T.inkSoft : field ? T.greenDark : T.orangeText, marginTop: 1 }}>
                            {isSkip
                              ? <span><i className="fa-solid fa-ban" style={{ fontSize: 8, marginRight: 4 }} />Skipped</span>
                              : field
                                ? <span><i className={`fa-solid fa-${transformRules[field.name] ? "wand-magic-sparkles" : "arrow-right-long"}`} style={{ fontSize: 8, marginRight: 4 }} />{field.name}</span>
                                : <span><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 8, marginRight: 4 }} />Decide</span>}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rowIndices.map(r => (
                    <tr key={r}>
                      {headers.map((h, idx) => {
                        const hit = !!query && String(csv.rows[r][h] ?? "").toLowerCase().includes(query);
                        return (
                          <td
                            key={idx}
                            onMouseEnter={() => setHover(h)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => togglePin(h)}
                            style={{
                              padding: "5px 6px", font: `${hit ? 600 : 400} 11px/15px ${T.fontData}`, color: hit ? T.ink : T.inkMuted,
                              borderBottom: `1px solid ${T.borderSubtle}`, borderRight: `1px solid ${T.borderSubtle}`,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer",
                              background: isActive(h) ? T.blue50 : hit ? T.tint.warning : T.white,
                              transition: "background 120ms ease-out",
                            }}
                          >
                            {csv.rows[r][h]}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DataSheet>
        </div>

        {/* Mapping list — single centered column */}
        <div style={{ maxWidth: 900, width: "100%", display: "flex", flexDirection: "column", gap: 14, margin: "4px auto 0" }}>
          {/* Column header pills */}
          <div style={{ ...ROW_GRID, padding: "0 12px 2px" }}>
            <span />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 26, padding: "0 12px", borderRadius: T.radiusPill, background: T.blue50, border: `1px solid ${T.blue200}`, whiteSpace: "nowrap" }}>
                <img src={fmxLogoBlue} alt="" style={{ height: 14, display: "block" }} />
                <span style={{ font: `600 13px/16px ${T.fontSans}`, color: T.brand }}>FMX Fields</span>
              </span>
            </div>
            <i className="fa-solid fa-arrow-left-long" style={{ fontSize: 12, color: T.inkSoft, textAlign: "center" }} />
            <div style={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 26, padding: "0 12px", borderRadius: T.radiusPill, background: T.white, border: `1px solid ${T.borderStrong}`, whiteSpace: "nowrap", minWidth: 0 }}>
                <i className={`fa-solid fa-${fileIcon(fileInfo)}`} style={{ fontSize: 12, color: T.inkMuted }} />
                <span style={{ font: `600 13px/16px ${T.fontSans}`, color: T.ink }}>Mapping from</span>
                <span style={{ font: `500 12px/16px ${T.fontData}`, color: T.inkMuted, overflow: "hidden", textOverflow: "ellipsis" }}>{fileName}</span>
              </span>
            </div>
          </div>

          {visibleGroups.map(([group, fields]) => (
            <div key={group}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                <span style={GROUP_LABEL}>{group}</span>
                <span style={{ font: `600 11px ${T.fontSans}`, color: T.inkSoft }}>
                  {fields.filter(f => mapping[f.name] || transformRules[f.name]).length}/{fields.length} mapped
                </span>
              </div>
              <div style={{ ...CARD, overflow: "hidden" }}>
                {fields.map((f, i) => {
                  const src = mapping[f.name];
                  const rule = transformRules[f.name];
                  const on = src ? isActive(src) : false;
                  const alt = i % 2 === 1;
                  const savedRule = !rule && savedRules?.[f.name];
                  const typeLabel = getTypeLabel(f);
                  const dropHover = dropField === f.name;
                  const justDropped = !rule && dropNotes[f.name] && dropNotes[f.name] === src;
                  return (
                    <div
                      key={f.name}
                      onMouseEnter={() => src && setHover(src)}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => togglePin(src)}
                      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; if (dropField !== f.name) setDropField(f.name); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropField(d => (d === f.name ? null : d)); }}
                      onDrop={e => { e.preventDefault(); dropOnField(f.name, e.dataTransfer.getData("text/plain") || dragCol); }}
                      style={{
                        borderBottom: i === fields.length - 1 ? "none" : `1px solid ${T.borderSubtle}`,
                        padding: "7px 12px",
                        background: dropHover ? T.blue50 : on ? T.blue50 : rule ? T.tint.info : alt ? T.bg : T.white,
                        boxShadow: (on || rule) && !dropHover ? `inset 2px 0 0 ${T.brand}` : "none",
                        outline: dropHover ? `2px dashed ${T.brand}` : dragCol ? `1.5px dashed ${T.blue200}` : "none",
                        outlineOffset: -2,
                        cursor: src ? "pointer" : "default",
                        transition: "background 120ms ease-out",
                      }}
                    >
                      <div style={ROW_GRID}>
                        {rule || src
                          ? <i className="fa-solid fa-circle-check" style={{ fontSize: 12, color: T.green500 }} />
                          : f.required
                            ? <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 12, color: T.orange500 }} />
                            : <i className="fa-regular fa-circle" style={{ fontSize: 12, color: T.border }} />}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "baseline", gap: 8 }}>
                            <span>
                              <span style={{ font: `600 13px ${T.fontSans}`, color: T.ink }}>{f.name}</span>
                              {f.required && <span style={{ color: T.red500, fontWeight: 700 }}> *</span>}
                            </span>
                            {!rule && (
                              <button
                                onClick={e => { e.stopPropagation(); openTransformModal(f.name); }}
                                style={{ background: "none", border: "none", cursor: "pointer", font: `600 11px ${T.fontSans}`, color: T.brand, padding: 0, flexShrink: 0 }}
                              >
                                <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 9, marginRight: 4 }} />
                                Add rule
                              </button>
                            )}
                          </div>
                          <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
                            {f.isCustomField
                              ? <select
                                  style={{ fontSize: 10, padding: "1px 4px", border: `1px solid ${T.border}`, borderRadius: 3, background: T.bg, color: T.inkMuted, cursor: "pointer" }}
                                  value={f.fieldType || "Text"}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => onCustomFieldTypeChange?.(f.customFieldId, e.target.value)}
                                >
                                  {Object.entries(FMX_FIELD_TYPE_MAP).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                              : typeLabel && <span style={TAG}>{typeLabel}</span>}
                            {f.crossSheet && (
                              <span style={{ fontSize: 10, color: T.cyan, whiteSpace: "nowrap" }}>
                                <i className="fa-solid fa-arrow-turn-up fa-rotate-90" style={{ fontSize: 8, marginRight: 3 }} />
                                {f.crossSheet}
                              </span>
                            )}
                          </div>
                        </div>
                        <i className="fa-solid fa-arrow-left-long" style={{ fontSize: 11, color: T.inkSoft }} />
                        {rule
                          ? (
                            <button
                              onClick={e => { e.stopPropagation(); openTransformModal(f.name); }}
                              style={{ flex: 1, minWidth: 0, height: 30, border: dropHover ? `1px dashed ${T.brand}` : dragCol ? `1px dashed ${T.blue200}` : `1px solid ${T.blue200}`, borderRadius: T.radiusBtn, background: dropHover ? T.blue50 : T.white, display: "flex", alignItems: "center", gap: 8, padding: "0 10px", cursor: "pointer", textAlign: "left" }}
                            >
                              <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 11, color: T.brand, flexShrink: 0 }} />
                              <span style={{ flex: 1, font: `500 13px ${T.fontSans}`, color: T.brand, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {dropHover
                                  ? "Drop to map this column"
                                  : <>Rule applied{src ? <> — <span style={{ font: `500 13px ${T.fontData}` }}>{src}</span></> : null}</>}
                              </span>
                              <span style={{ font: `600 11px ${T.fontSans}`, color: T.brand, flexShrink: 0 }}>Edit</span>
                            </button>
                          )
                          : dragCol
                            ? (
                              // While a header drag is active, swap the native select
                              // for a drop target the dashed states can style.
                              <div style={{ ...SOURCE_SELECT, display: "flex", alignItems: "center", border: dropHover ? `1px dashed ${T.brand}` : `1px dashed ${T.blue200}`, boxShadow: dropHover ? `inset 0 0 0 1px ${T.brand}` : "none", background: dropHover ? T.blue50 : T.white, color: dropHover ? T.brand : src ? T.ink : T.inkSoft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "copy" }}>
                                {dropHover ? "Drop to map this column" : src || "— not mapped —"}
                              </div>
                            )
                            : (
                              <select
                                style={SOURCE_SELECT}
                                value={src ?? ""}
                                onClick={e => e.stopPropagation()}
                                onChange={e => { const v = e.target.value || undefined; setMapping(m => ({ ...m, [f.name]: v })); setDropNotes(n => (n[f.name] ? { ...n, [f.name]: undefined } : n)); }}
                                onFocus={e => { e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.boxShadow = T.focusRing; }}
                                onBlur={e => { e.currentTarget.style.borderColor = T.borderStrong; e.currentTarget.style.boxShadow = "none"; }}
                              >
                                <option value="">— not mapped —</option>
                                {/* Key by index so duplicate/blank header names all render */}
                                {headers.map((h, idx) => (
                                  <option key={idx} value={h}>{h === "" ? "(blank column)" : h}</option>
                                ))}
                              </select>
                            )}
                      </div>
                      {rule && noteLine(T.brand, "wand-magic-sparkles", <>Rule: {rule.instruction}</>)}
                      {justDropped && noteLine(T.greenDark, "circle-check", <>Updated — now mapped from "{src === "" ? "(blank column)" : src}"</>)}
                      {savedRule && noteLine(T.greenText, "clock-rotate-left", (
                        <button
                          onClick={e => { e.stopPropagation(); openTransformModal(f.name, savedRule); }}
                          style={{ background: "none", border: "none", cursor: "pointer", font: `500 11.5px/15px ${T.fontSans}`, color: T.greenText, padding: 0, textDecoration: "underline" }}
                        >
                          Saved rule from a previous import — review &amp; apply
                        </button>
                      ))}
                      {!rule && mappingSources?.[f.name] === "memory" && src && (
                        noteLine(T.inkMuted, "circle-info", <>Same as your last {memoryMatches?.[src]?.confidence ?? 2} imports</>)
                      )}
                      {refNotes[f.name] && noteLine(T.inkMuted, "circle-info", refNotes[f.name])}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Unmapped columns — FMX-field picker on the left, source column on the right */}
          <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                <span style={{ ...GROUP_LABEL, color: sectionCols.length ? T.orangeText : T.greenDark }}>Unmapped columns</span>
                <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkMuted }}>— pick the FMX field each column belongs in, drag its header onto a field above, or skip it</span>
                {emptyUnmappedCount > 0 && (
                  <label style={{ marginLeft: "auto", fontSize: 11, color: T.inkSoft, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={hideEmptyUnmapped} onChange={e => setHideEmptyUnmapped(e.target.checked)} style={{ cursor: "pointer" }} />
                    Hide empty ({emptyUnmappedCount})
                  </label>
                )}
              </div>
              {/* No overflow:hidden here — the field-picker popover must escape the card */}
              <div style={{ ...CARD, border: `1px solid ${sectionCols.some(h => !assignedField(h) && !skipped[h]) ? T.orange500 : T.green500}` }}>
                {displayedUnmapped.length === 0 && (
                  <div style={{ padding: 14, font: `500 13px ${T.fontSans}`, color: T.greenDark, display: "flex", alignItems: "center", gap: 8 }}>
                    <i className="fa-solid fa-circle-check" />
                    Every column is mapped — nothing left to decide.
                  </div>
                )}
                {displayedUnmapped.map((h, i) => {
                  const p = colProfiles[h] || { filled: 0, unique: 0, samples: [], dtype: "Empty" };
                  const fld = assignedField(h);
                  const isSkip = !fld && !!skipped[h];
                  const on = isActive(h);
                  const first = i === 0;
                  const last = i === displayedUnmapped.length - 1;
                  const options = pickerOptionsFor(h);
                  return (
                    <div
                      key={i}
                      onMouseEnter={() => setHover(h)}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => togglePin(h)}
                      style={{
                        borderBottom: last ? "none" : `1px solid ${T.borderSubtle}`,
                        padding: "7px 12px",
                        borderRadius: `${first ? innerRadius : 0}px ${first ? innerRadius : 0}px ${last ? innerRadius : 0}px ${last ? innerRadius : 0}px`,
                        background: on ? T.blue50 : isSkip ? T.bg : fld ? T.white : T.badge.orange,
                        boxShadow: on ? `inset 2px 0 0 ${T.brand}` : `inset 2px 0 0 ${fld ? T.green500 : isSkip ? T.border : T.orange500}`,
                        cursor: "pointer",
                        transition: "background 120ms ease-out",
                      }}
                    >
                      <div style={ROW_GRID}>
                        <i className={`fa-solid fa-${fld ? "circle-check" : isSkip ? "ban" : "circle-exclamation"}`} style={{ fontSize: 12, color: fld ? T.green500 : isSkip ? T.inkSoft : T.orange500 }} />
                        <FieldPicker
                          value={fld}
                          isSkip={isSkip}
                          accent={!fld && !isSkip}
                          options={options}
                          suggested={suggestionFor(h, options)}
                          schemaLabel={`FMX ${baseType} fields`}
                          getTypeLabel={getTypeLabel}
                          onPick={fieldName => assignField(h, fieldName)}
                          onSkip={() => { if (!isSkip) toggleSkip(h); }}
                        />
                        <i className="fa-solid fa-arrow-right-long" style={{ fontSize: 11, color: T.inkSoft }} />
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <div style={{ minWidth: 0, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <span style={{ font: `600 13px ${T.fontData}`, color: isSkip ? T.inkSoft : T.ink }}>{h === "" ? "(blank column)" : h}</span>
                          </div>
                          <Button
                            variant="neutral"
                            size="sm"
                            icon={isSkip ? "rotate-left" : "ban"}
                            onClick={e => { e.stopPropagation(); toggleSkip(h); }}
                            style={{ flexShrink: 0 }}
                          >
                            {isSkip ? "Undo" : "Skip"}
                          </Button>
                        </div>
                      </div>
                      <div style={{ font: `400 11.5px/15px ${T.fontSans}`, color: T.inkMuted, padding: "4px 0 1px 23px" }}>
                        {fld
                          ? <>Will import into <b style={{ color: T.ink }}>{fld}</b> — {p.filled} of {csv.rows.length} rows filled.</>
                          : isSkip
                            ? <>Won't import — the data stays in your file. You can undo this anytime.</>
                            : p.filled === 0
                              ? <>No data in this column.{displaced[h] ? <> Was mapped to <b style={{ color: T.ink }}>{displaced[h]}</b>.</> : null} Pick the FMX field it belongs in, or skip it.</>
                              : <>{p.filled} of {csv.rows.length} rows filled · {displaced[h] ? <>was mapped to <b style={{ color: T.ink }}>{displaced[h]}</b></> : <>{p.unique} unique value{p.unique === 1 ? "" : "s"}</>} — e.g. {p.samples.map(s => `"${s}"`).join(", ")}. Pick the FMX field it belongs in, or skip it.</>}
                      </div>
                    </div>
                  );
                })}
              </div>
          </div>

          {/* Required-fields summary */}
          <div style={{ ...CARD, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, font: `600 13px ${T.fontSans}`, color: allRequiredMapped ? T.green500 : T.orangeText }}>
              <i className={`fa-solid fa-${allRequiredMapped ? "circle-check" : "circle-exclamation"}`} />
              {requiredFields.length === 0
                ? "No required fields for this entity type"
                : allRequiredMapped
                  ? `All ${requiredFields.length} required field${requiredFields.length === 1 ? "" : "s"} mapped`
                  : `${requiredFields.length - unresolvedRequired.length} of ${requiredFields.length} required fields mapped`}
            </div>
            <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 6 }}>
              {requiredFields.length > 0 && allRequiredMapped && <>{joinNames(requiredFields)} {requiredFields.length === 1 ? "is" : "are"} resolved</>}
              {!allRequiredMapped && <>Still needed: {joinNames(unresolvedRequired)}. </>}
              {decideCount > 0
                ? <>{allRequiredMapped ? ". " : ""}Decide on the {decideCount} orange row{decideCount === 1 ? "" : "s"}, or continue — they'll be skipped.</>
                : allRequiredMapped && <> — and every column has a decision. You're ready to continue.</>}
            </div>
            {onRerunMapping && (
              <Button
                variant="secondary"
                size="sm"
                icon="rotate"
                disabled={aiLoading}
                onClick={onRerunMapping}
                style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
              >
                {aiLoading ? "Re-running mapping…" : "Re-run mapping"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
